/**
 * TanStack Query hooks for identity: the launch payload, first run, avatars and account deletion.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiFetch, rawApiFetch, uploadApiFetch, BASE_URL } from '../client'
import type { ApiError } from '../client'
import type { components } from '../generated/schema'
import type { UserProfileOut } from '../types'
import { OPTIONAL_QUERY_DEFAULTS } from './shared'

// ── Spec 2: identity, first run, avatars, deletion ──────────────────────

type MeOut = components['schemas']['MeOut']
type FirstRunDraftIn = components['schemas']['FirstRunDraftIn']
type FirstRunDraftOut = components['schemas']['FirstRunDraftOut']

/**
 * The launch payload. Both the routing guard and the profile screens read it.
 *
 * `staleTime: 0` overrides the global `Infinity`, deliberately: this decides
 * whether the app is reachable at all, and a cached `first_run_complete: false`
 * would bounce a user who has just finished back into first run.
 */
export function useMe() {
  return useQuery({
    queryKey: ['me'],
    queryFn: () => apiFetch<MeOut>('/api/me'),
    staleTime: 0,
  })
}

/** One step of first run. Partial by design; the server merges. */
export function useSaveFirstRunStep() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: FirstRunDraftIn) =>
      apiFetch<FirstRunDraftOut>('/api/profile/first-run/draft', {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['me'] }),
  })
}

/** Creates the profile from a complete draft. Takes no body. */
export function useCompleteFirstRun() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () =>
      apiFetch<UserProfileOut>('/api/profile/first-run', { method: 'POST' }),
    // MUST return this promise, not just call it: `useMutation` awaits
    // `onSuccess` before resolving `mutateAsync()`, but only if `onSuccess`
    // itself returns a promise. `RequireFirstRun` (every authenticated route's
    // gate) reads `['me'].first_run_complete` on every render; a caller that
    // awaits `mutateAsync()` and then navigates -- which this hook's own
    // invalidation of `['me']` explicitly invites -- must see the refreshed
    // value the instant it lands, not the pre-completion cached one. Do not
    // "simplify" this back to a bare block statement: that silently drops the
    // await and reopens the exact race FirstRun.tsx was bitten by (the client
    // bounces back to /first-run because it read `first_run_complete: false`
    // a moment before the real refetch resolved).
    onSuccess: () => Promise.all([
      queryClient.invalidateQueries({ queryKey: ['me'] }),
      queryClient.invalidateQueries({ queryKey: ['profile'] }),
    ]),
  })
}

/**
 * UNREFERENCED SINCE 2026-08-18 (FR-D31). First run was its only consumer, and
 * the picture step left the flow; `FirstRun.tsx` held onto the mutation for a
 * while afterwards without ever calling it, which made `isPending` a term that
 * was permanently false in its `isSaving`.
 *
 * Kept for the same reason `PictureStep.tsx` is: it is the draft half of the
 * avatar upload path, `POST /api/profile/first-run/draft/avatar` still exists,
 * and stage 4's Personal Details is where both land. Delete the pair together
 * if stage 4 goes another way.
 *
 * Note this is the DRAFT route, which only makes sense before a profile
 * exists. `useUploadAvatar` is the one Personal Details will want for an
 * established account; if stage 4 has no pre-profile photo step, this hook is
 * the one of the two that has no future.
 */
export function useUploadDraftAvatar() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (file: File) =>
      uploadApiFetch('/api/profile/first-run/draft/avatar', file),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['me'] }),
  })
}

/**
 * Fetches the current user's avatar image as a Blob, or `null` when they
 * have none.
 *
 * `apiFetch` cannot be used here: it ends in `res.json()`, which throws on
 * image bytes. `rawApiFetch` takes a full URL (it applies no base-URL
 * prefix) and attaches the bearer token -- which is the entire reason a
 * plain `<img src="/api/profile/avatar">` cannot work, since the token lives
 * in localStorage, not a cookie a browser would send on its own.
 *
 * A 404 means "no avatar" and is the NORMAL state (D10): the monogram is the
 * client's default identity, not a fallback for a broken photo. It resolves
 * to `null` rather than rejecting the query, and `retry: false` (via
 * `OPTIONAL_QUERY_DEFAULTS`) stops a genuine failure from burning retries on
 * what would otherwise look like an expected-404 query.
 *
 * The query key includes `userId` on purpose. The endpoint's URL is
 * identical for every user and its response varies only by the
 * `Authorization` header (`Vary: Authorization`, api/routers/profile.py) --
 * a header `fetch()` never puts in its cache key. Keying on a constant would
 * let a sign-out/sign-in swap on the same browser serve a cached blob across
 * accounts. `useUploadAvatar`/`useDeleteAvatar` already invalidate
 * `['avatar']`; TanStack Query's default partial matching still invalidates
 * `['avatar', userId]`, since `['avatar']` is a prefix of it.
 */
export function useAvatarBlob(userId: number) {
  return useQuery({
    queryKey: ['avatar', userId],
    queryFn: async (): Promise<Blob | null> => {
      const url = `${BASE_URL}/api/profile/avatar`
      const res = await rawApiFetch(url)
      if (res.status === 404) return null
      if (!res.ok) {
        const body = await res.text().catch(() => '')
        const error: ApiError = { status: res.status, message: body || res.statusText, url }
        throw error
      }
      return res.blob()
    },
    ...OPTIONAL_QUERY_DEFAULTS,
  })
}

export function useUploadAvatar() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (file: File) => uploadApiFetch('/api/profile/avatar', file),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['avatar'] }),
  })
}

export function useDeleteAvatar() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => apiFetch<void>('/api/profile/avatar', { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['avatar'] }),
  })
}

export function useDeleteAccount() {
  return useMutation({
    mutationFn: () => apiFetch<void>('/api/me', { method: 'DELETE' }),
  })
}

/**
 * "Keep my account" (#365): ends a pending deletion. The only way back; signing in never does it.
 *
 * **Resets every query, deliberately, and this is the one place that does.** While the account was
 * pending, every request but `/api/me` answered 403 and TanStack kept those failures (`staleTime` is
 * `Infinity`, so a cached error would not be asked again); the account is a different account now,
 * so nothing read under the old answer may stand. `resetQueries` returns each query to its initial
 * state and asks again for the ones on screen, `['me']` first, and the pending screen gives way to
 * the app when that answers.
 */
export function useKeepAccount() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: () => apiFetch<void>('/api/me/keep', { method: 'POST' }),
    onSuccess: () => queryClient.resetQueries(),
  })
}
